import { msForSamples } from '@hub/contracts'
import type { AudioSegment } from './meeting-client'
import { downsampleToPcm16, LiveVad, type LiveChunk } from './segmenter'
import { MIC_WORKLET_NAME, micWorkletUrl } from './worklet'

/**
 * The browser half of live transcription (PLAN-085 §1.6): this tab's **own**
 * microphone → 16 kHz PCM16 → voice-activity segments → binary frames on the
 * room socket, frame by frame while someone speaks (card hglk step B).
 *
 * One browser is one speaker, which is what makes speaker attribution free:
 * whatever this tab sends is attributed to the person logged into it. Nothing
 * is sent while the microphone is muted.
 */
export class MicTranscriber {
  #context: AudioContext | null = null
  #node: AudioWorkletNode | null = null
  #source: MediaStreamAudioSourceNode | null = null
  // Random so a VAD rebuilt on a mute/unmute does not reuse ids.
  #vad = new LiveVad({ firstUtteranceId: Math.floor(Math.random() * 0x7fff_ffff) })
  #enabled = true
  #stopped = false

  /**
   * `onSegment` gets PCM and a duration — no sequence number and no position.
   * The socket assigns the sequence (it must be continuous across the
   * transcriber restarts a mute/unmute causes, REVIEW-1 #5) and the room
   * assigns the time (this clock is per transcriber, not per meeting,
   * REVIEW-2 #1).
   */
  constructor(private readonly onSegment: (segment: AudioSegment) => void) {}

  /** Start capturing from a live microphone track. */
  async start(track: MediaStreamTrack): Promise<void> {
    if (this.#stopped || this.#context) return
    const context = new AudioContext()
    this.#context = context
    await context.audioWorklet.addModule(micWorkletUrl())
    if (this.#stopped) return

    const node = new AudioWorkletNode(context, MIC_WORKLET_NAME)
    this.#node = node
    node.port.onmessage = (event: MessageEvent<Float32Array>) => this.#onFrame(event.data, context)

    const source = context.createMediaStreamSource(new MediaStream([track]))
    this.#source = source
    source.connect(node)
    // The worklet produces no output; connecting it to the destination would
    // play the speaker's own voice back at them.
  }

  /** Mute: the segmenter is flushed so a half-spoken sentence still lands. */
  setEnabled(enabled: boolean): void {
    if (this.#enabled === enabled) return
    this.#enabled = enabled
    this.#node?.port.postMessage({ enabled })
    if (!enabled) this.#emit(this.#vad.flush())
  }

  async stop(): Promise<void> {
    this.#stopped = true
    this.#emit(this.#vad.flush())
    this.#node?.port.close()
    this.#source?.disconnect()
    this.#node?.disconnect()
    await this.#context?.close().catch(() => {})
    this.#context = null
    this.#node = null
    this.#source = null
  }

  #onFrame(frame: Float32Array, context: AudioContext): void {
    if (!this.#enabled || this.#stopped) return
    this.#emit(this.#vad.push(downsampleToPcm16(frame, context.sampleRate)))
  }

  #emit(chunks: LiveChunk[]): void {
    for (const chunk of chunks) {
      const segment: AudioSegment = { ...chunk, durationMs: msForSamples(chunk.pcm.length) }
      this.onSegment(segment)
    }
  }
}
